/**
 * SendSMSModal — the operator SMS composer.
 *
 * Replaces the placeholder that showed a fake delay and three hardcoded templates.
 *
 * Sending follows the environment feature flag; QA composition never dispatches SMS.
 *
 * What this screen is responsible for getting right:
 *   * the SEGMENT COUNT the member's carrier will actually bill, including the opt-out footer the
 *     server appends. A 150-character message is two segments once the footer is added, and an
 *     operator who cannot see that will write one without meaning to.
 *   * showing WHERE the splits fall, so a message is not cut mid-sentence.
 *   * the DISCLOSURE for a contact who has not opted in, bound to the version displayed.
 *   * the DESTINATION shown, echoed back so the send refuses if the number changed underneath.
 *   * MMS (migration 2026-09-28-008): ONE image, pasted or attached. The preview is the exact image that
 *     will be sent (re-encoded if needed); only the upload the agent is looking at can be sent (every
 *     replace/remove supersedes in-flight uploads); a different image is a different message (new
 *     request key). Whether images can be sent at all is the SERVER's answer, not the browser's.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../common/Modal";
import {
  populateSmsTemplate,
  unresolvedSmsVariables,
  smsFriendlyCharacters,
} from "../../utils/smsTemplateVariables";
import SmsTemplateManager from "./SmsTemplateManager";
import { isFeatureEnabled } from "../../utils/featureFlags";
import { useToast } from "../ui/Toast";
import type { Contact } from "../../types/contact.types";
import {
  SENDING_ENABLED,
  preflight,
  segmentPlan,
  listTemplates,
  sendOperatorSms,
  getMmsStatus,
  uploadSmsMedia,
  type MmsStatus,
  type SmsPreflight,
  type SmsSegmentPlan,
  type SmsTemplate,
} from "../../services/sms.service";
import {
  MMS_MAX_TEXT_CHARS,
  formatBytes,
  prepareImageForMms,
} from "../../utils/smsImage";

interface Attachment {
  seq: number;
  previewUrl: string;
  bytes: number;
  type: string;
  width: number;
  height: number;
  reencoded: boolean;
  status: "uploading" | "ready" | "error";
  mediaId?: string;
  error?: string;
}

interface SendSMSModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact;
  agentName?: string;
}

/** One request key per composed message, so a double-click cannot become two sends. */
/** Picture-message controls are shown only where the flag is on (off in production until MMS is live). */
const MMS_UI = isFeatureEnabled("operatorMms");

const newRequestKey = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const SendSMSModal: React.FC<SendSMSModalProps> = ({
  isOpen,
  onClose,
  contact,
  agentName,
}) => {
  const { showToast } = useToast();
  const [message, setMessage] = useState("");
  const [pre, setPre] = useState<SmsPreflight | null>(null);
  const [planBody, setPlanBody] = useState("");
  const [planError, setPlanError] = useState(false);
  const [templateDraft, setTemplateDraft] = useState(false);
  const [templateFolder, setTemplateFolder] = useState("__all");
  const [plan, setPlan] = useState<SmsSegmentPlan | null>(null);
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [managingTemplates, setManagingTemplates] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestKey, setRequestKey] = useState(newRequestKey);
  const [mms, setMms] = useState<MmsStatus | null>(null);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [attachNote, setAttachNote] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState<string | null>(null);
  /** Bumped by every attach / replace / remove / reset: an upload that finishes late is ignored. */
  const attachSeq = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const clearAttachment = () => {
    attachSeq.current += 1;
    setAttachment((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  };

  useEffect(() => {
    if (!isOpen) {
      clearAttachment();
      return;
    }
    let active = true;
    setError(null);
    setTemplateError(null);
    setAck(false);
    setMessage("");
    setPre(null);
    setPlan(null);
    setTemplateId(null);
    setTemplateDraft(false);
    setTemplateFolder("__all");
    setManagingTemplates(false);
    setTemplates([]);
    setRequestKey(newRequestKey());
    clearAttachment();
    setAttachNote(null);
    setUnknownOutcome(null);
    setMms(null);
    (MMS_UI
      ? getMmsStatus()
      : Promise.resolve({
          enabled: false,
          reason: "hidden",
          maxBytes: 1_000_000,
        })
    )
      .then((m) => {
        if (active) {
          setMms(m);
        }
      })
      .catch(() => {
        if (active) {
          setMms({
            enabled: false,
            reason: "unavailable",
            maxBytes: 1_000_000,
          });
        }
      });
    setLoading(true);
    preflight(contact.id)
      .then((p) => {
        if (active) {
          setPre(p);
        }
      })
      .catch((e) => {
        if (active) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    listTemplates()
      .then((t) => {
        if (active) {
          setTemplates(t);
        }
      })
      .catch((e) => {
        if (active) {
          setTemplateError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      active = false;
    };
  }, [isOpen, contact.id]);

  // The plan is computed SERVER-side so the composer and the send cannot disagree about what a
  // segment is. GSM-7 vs UCS-2 and the extension characters that cost two units are not something
  // to re-implement in the browser and hope it matches.
  useEffect(() => {
    let active = true;
    setPlan(null);
    setPlanBody("");
    setPlanError(false);
    if (!pre || !message.trim()) {
      return;
    }
    const full = message.trim() + (pre.footer_text ?? "");
    const timer = setTimeout(() => {
      segmentPlan(full)
        .then((result) => {
          if (active) {
            setPlan(result);
            setPlanBody(full);
          }
        })
        .catch(() => {
          if (active) {
            setPlanError(true);
          }
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [message, pre]);

  const missingVariables = templateDraft ? unresolvedSmsVariables(message) : [];
  const folders = [
    ...new Set(
      templates.map((t) => t.folder_name).filter((v): v is string => !!v),
    ),
  ].sort();
  const isMms = attachment !== null;
  const footer = pre?.footer_text ?? "";
  const mmsText = message.trim() ? message.trim() + footer : footer.trim();
  const mmsUnavailable =
    mms && !mms.enabled
      ? "Sending images isn't available yet. You can still send this as a text."
      : null;

  const attachFile = async (file: File) => {
    setAttachNote(null);
    if (!mms?.enabled) {
      setAttachNote(mmsUnavailable ?? "Checking whether images can be sent…");
      return;
    }
    const seq = ++attachSeq.current;
    // A different image is a different message: never reuse the previous request key for it.
    setRequestKey(newRequestKey());
    let prepared;
    try {
      prepared = await prepareImageForMms(file, mms.maxBytes);
    } catch (e) {
      if (seq === attachSeq.current) {
        setAttachment((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev.previewUrl);
          }
          return null;
        });
        setAttachNote(e instanceof Error ? e.message : String(e));
      }
      return;
    }
    if (seq !== attachSeq.current) {
      return;
    }
    const previewUrl = URL.createObjectURL(prepared.blob);
    setAttachment((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return {
        seq,
        previewUrl,
        bytes: prepared.blob.size,
        type: prepared.type,
        width: prepared.width,
        height: prepared.height,
        reencoded: prepared.reencoded,
        status: "uploading",
      };
    });
    try {
      const up = await uploadSmsMedia(contact.id, prepared.blob, prepared.type);
      // Replaced, removed or the composer reset meanwhile: this upload is abandoned (the server's
      // cleanup retires it later). It can never be the image that is sent.
      if (seq !== attachSeq.current) {
        return;
      }
      setAttachment((a) =>
        a && a.seq === seq
          ? { ...a, status: "ready", mediaId: up.media_id }
          : a,
      );
    } catch (e) {
      if (seq !== attachSeq.current) {
        return;
      }
      setAttachment((a) =>
        a && a.seq === seq
          ? {
              ...a,
              status: "error",
              error: e instanceof Error ? e.message : String(e),
            }
          : a,
      );
    }
  };

  const removeAttachment = () => {
    clearAttachment();
    setAttachNote(null);
    setRequestKey(newRequestKey());
  };

  /** Ordinary text paste is untouched; a clipboard holding only image(s) attaches the first. */
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!MMS_UI) {
      return;
    }
    const text = e.clipboardData.getData("text/plain");
    const images = Array.from(e.clipboardData.files ?? []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (images.length === 0 || text) {
      return;
    }
    e.preventDefault();
    void attachFile(images[0]);
    if (images.length > 1) {
      setAttachNote(
        "Only one image can be sent per message. The first pasted image was attached.",
      );
    }
  };

  const needsDisclosure = pre?.requires_disclosure ?? false;
  const blocked = pre ? !pre.allowed : false;

  const blockedReason = useMemo(() => {
    switch (pre?.consent_state) {
      case "opted_out":
        return "This contact has opted out of text messages. If the member requests re-subscription, record their conversation using Update SMS consent on the contact overview.";
      case "number_suppressed":
        return "SMS is blocked for this phone number. An opt-out can be recorded on another contact sharing the number. Use Update SMS consent on the contact overview to record a member-requested re-subscription.";
      case "no_usable_phone":
        return "This contact has no phone number that can receive a text.";
      default:
        return null;
    }
  }, [pre?.consent_state]);

  const canSend =
    !!pre &&
    !blocked &&
    !loading &&
    (!needsDisclosure || ack) &&
    missingVariables.length === 0 &&
    !sending &&
    !unknownOutcome &&
    (isMms
      ? attachment.status === "ready" &&
        !!attachment.mediaId &&
        mmsText.length <= MMS_MAX_TEXT_CHARS
      : message.trim().length > 0 &&
        !!plan &&
        planBody === message.trim() + (pre.footer_text ?? "") &&
        plan.segments <= 6);

  const handleSend = async () => {
    if (!pre || !canSend) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      const outcome = await sendOperatorSms({
        contactId: contact.id,
        body: message.trim(),
        templateId,
        disclosureAck: needsDisclosure ? ack : false,
        disclosureVersion: needsDisclosure ? pre.disclosure_version : null,
        expectedPhone: pre.phone_e164 ?? "",
        requestKey,
        mediaIds:
          isMms && attachment?.mediaId ? [attachment.mediaId] : undefined,
      });
      switch (outcome.kind) {
        case "accepted":
          showToast(
            "success",
            outcome.transport === "mms"
              ? "Picture message sent. The delivery report will confirm it arrived."
              : "Message sent",
          );
          onClose();
          break;
        case "already_sent":
          showToast("info", "This message was already sent.");
          onClose();
          break;
        case "not_sent":
          setError(`This message was not sent (${outcome.reason}).`);
          break;
        case "unknown":
          setUnknownOutcome(outcome.note);
          break;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  /** Render the body with the carrier's split points marked, plus the server-appended footer. */
  const preview = useMemo(() => {
    if (!pre) {
      return null;
    }
    const full = message.trim() + (pre.footer_text ?? "");
    const cuts = plan?.boundaries ?? [];
    if (cuts.length === 0) {
      return [full];
    }
    const parts: string[] = [];
    let last = 0;
    for (const c of cuts) {
      parts.push(full.slice(last, c));
      last = c;
    }
    parts.push(full.slice(last));
    return parts;
  }, [message, plan, pre]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send SMS">
      <div className="space-y-4">
        {!SENDING_ENABLED && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="font-medium">Sending is not enabled yet</div>
            <div className="mt-1">
              You can compose and check the message, but it will not be sent and
              nothing will be recorded against the contact.
            </div>
          </div>
        )}

        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-sm text-gray-500">To</div>
          <div className="font-medium text-gray-900">
            {contact.first_name} {contact.last_name}
          </div>
          <div className="text-sm text-gray-600">
            {loading
              ? "Checking…"
              : (pre?.phone_masked ?? "No usable phone number")}
          </div>
        </div>

        {blockedReason && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {blockedReason}
          </div>
        )}

        {pre && (
          <button
            type="button"
            disabled={sending}
            onClick={() => setManagingTemplates((v) => !v)}
            className="text-sm font-medium text-blue-700"
          >
            {managingTemplates
              ? "Close template manager"
              : "Manage SMS templates"}
          </button>
        )}
        {managingTemplates && (
          <SmsTemplateManager
            templates={templates}
            onClose={() => setManagingTemplates(false)}
            onSaved={(saved) =>
              setTemplates((rows) =>
                [...rows.filter((t) => t.id !== saved.id), saved].sort((a, b) =>
                  a.name.localeCompare(b.name),
                ),
              )
            }
            onArchived={(id) => {
              setTemplates((rows) => rows.filter((t) => t.id !== id));
              if (templateId === id) {
                setTemplateId(null);
              }
            }}
          />
        )}
        {templateError && (
          <p role="alert" className="text-sm text-red-700">
            Templates could not be loaded: {templateError}
          </p>
        )}
        {templates.length > 0 && !blocked && (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Templates
            </label>
            <label className="mb-2 block text-sm">
              Template folder
              <select
                value={templateFolder}
                disabled={sending}
                onChange={(e) => setTemplateFolder(e.target.value)}
                className="ml-2 rounded border p-1"
              >
                <option value="__all">All folders</option>
                <option value="">Unfiled</option>
                {folders.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap gap-2">
              {templates
                .filter(
                  (t) =>
                    templateFolder === "__all" ||
                    (t.folder_name ?? "") === templateFolder,
                )
                .map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={sending}
                    onClick={() => {
                      setMessage(
                        populateSmsTemplate(t.body, contact, agentName),
                      );
                      setTemplateDraft(true);
                      setTemplateId(t.id);
                    }}
                    className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
                    title={t.description ?? undefined}
                  >
                    {t.name}
                  </button>
                ))}
            </div>
          </div>
        )}

        {!blocked && (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Message
            </label>
            <p className="mb-2 text-xs text-gray-600">
              Edit this message for this send. Your edits do not change the
              saved template.
            </p>
            <textarea
              aria-label="Message"
              disabled={sending}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPaste={handlePaste}
              rows={4}
              placeholder={
                mms?.enabled
                  ? "Type your message, or paste an image…"
                  : "Type your message…"
              }
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {MMS_UI && (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                <span
                  data-testid="sms-mode"
                  className={
                    isMms
                      ? "rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800"
                      : "rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                  }
                >
                  {isMms ? "Picture message (MMS)" : "Text message (SMS)"}
                </span>
                <button
                  type="button"
                  disabled={sending || !mms?.enabled || !!unknownOutcome}
                  onClick={() => fileInput.current?.click()}
                  className="font-medium text-blue-700 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  {attachment ? "Replace image" : "Attach image"}
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png"
                  aria-label="Image file"
                  data-testid="sms-image-input"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) {
                      void attachFile(f);
                    }
                  }}
                />
                {mmsUnavailable && !attachment && (
                  <span className="text-xs text-gray-500">
                    {mmsUnavailable}
                  </span>
                )}
              </div>
            )}
            {attachNote && (
              <p role="alert" className="mt-2 text-sm text-amber-800">
                {attachNote}
              </p>
            )}
            {attachment && (
              <div
                data-testid="sms-attachment"
                className="mt-2 flex gap-3 rounded-lg border border-gray-200 p-2"
              >
                <img
                  src={attachment.previewUrl}
                  alt="Image that will be sent"
                  className="h-24 w-24 flex-none rounded object-cover"
                />
                <div className="min-w-0 text-xs text-gray-700">
                  <div
                    className={
                      attachment.status === "error"
                        ? "font-medium text-red-700"
                        : "font-medium"
                    }
                  >
                    {attachment.status === "uploading"
                      ? "Uploading…"
                      : attachment.status === "error"
                        ? `Upload failed: ${attachment.error}`
                        : "Ready to send"}
                  </div>
                  <div className="text-gray-500">
                    {attachment.width}×{attachment.height} ·{" "}
                    {formatBytes(attachment.bytes)} ·{" "}
                    {attachment.type === "image/png" ? "PNG" : "JPEG"}
                  </div>
                  {attachment.reencoded && (
                    <div className="text-gray-500">
                      Resized to fit the picture-message limit. This preview is
                      exactly what will be sent.
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={sending}
                    onClick={removeAttachment}
                    className="mt-1 font-medium text-blue-700"
                  >
                    Remove image
                  </button>
                </div>
              </div>
            )}
            {isMms && (
              <p
                className={
                  mmsText.length > MMS_MAX_TEXT_CHARS
                    ? "mt-2 text-xs font-medium text-red-700"
                    : "mt-2 text-xs text-gray-600"
                }
              >
                Sent as one picture message · {mmsText.length.toLocaleString()}{" "}
                / {MMS_MAX_TEXT_CHARS.toLocaleString()} characters including the
                opt-out text. Text is optional with an image.
              </p>
            )}

            {missingVariables.length > 0 && (
              <p role="alert" className="mt-2 text-sm text-amber-800">
                Complete these placeholders in the message before sending:{" "}
                {missingVariables.join(", ")}. Missing or ambiguous contact
                details are not guessed.
              </p>
            )}
            {planError && !isMms && (
              <p role="alert" className="mt-2 text-sm text-red-700">
                The segment count could not be checked. Edit the message to try
                again.
              </p>
            )}
            {plan?.encoding === "UCS2" &&
              smsFriendlyCharacters(message) !== message && (
                <div className="mt-2 text-xs text-gray-600">
                  <button
                    type="button"
                    disabled={sending}
                    onClick={() => setMessage(smsFriendlyCharacters(message))}
                    className="font-medium text-blue-700"
                  >
                    Use SMS-friendly characters
                  </button>
                  <span>
                    {" "}
                    — replaces ® with (R), curly quotes with straight quotes,
                    and long dashes with hyphens in this draft to reduce
                    segments.
                  </span>
                </div>
              )}

            {/* The footer is appended by the SERVER and cannot be edited away, so it is shown as
                part of what will be sent rather than as a note underneath. */}
            {pre?.footer_text && (
              <div className="mt-2 rounded-md bg-gray-100 px-3 py-2 text-xs text-gray-600">
                <span className="font-medium">Always appended:</span>
                <span className="ml-1 whitespace-pre-wrap">
                  {pre.footer_text.trim()}
                </span>
              </div>
            )}

            {plan && !isMms && (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span
                  className={
                    plan.segments > 6
                      ? "font-medium text-red-700"
                      : plan.segments > 1
                        ? "font-medium text-amber-700"
                        : "text-gray-600"
                  }
                >
                  {plan.segments} segment{plan.segments === 1 ? "" : "s"} ·{" "}
                  {plan.units} units · {plan.encoding}
                </span>
                {plan.segments > 1 && (
                  <span className="text-gray-500">
                    splits after {plan.boundaries.join(", ")} characters
                  </span>
                )}
                {plan.segments > 6 && (
                  <span className="font-medium text-red-700">
                    over the 6-segment limit — shorten the message
                  </span>
                )}
              </div>
            )}

            {plan && !isMms && plan.segments > 1 && preview && (
              <div className="mt-2 space-y-1">
                <div className="text-xs font-medium text-gray-700">
                  How it arrives ({plan.segments} messages)
                </div>
                {preview.map((part, i) => (
                  <div
                    key={i}
                    className="rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-xs text-gray-800"
                  >
                    <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-400">
                      part {i + 1} of {preview.length}
                    </div>
                    <span className="whitespace-pre-wrap">{part}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {needsDisclosure && !blocked && (
          <div className="rounded-lg border border-blue-300 bg-blue-50 p-3">
            <div className="text-sm font-medium text-blue-900">
              This contact has not opted in to text messages
            </div>
            <div className="mt-2 whitespace-pre-wrap rounded bg-white/70 p-2 text-xs text-blue-900">
              {pre?.disclosure_text}
            </div>
            <label className="mt-2 flex items-start gap-2 text-sm text-blue-900">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I read this disclosure to the member and they agreed to receive
                text messages.
                {pre?.disclosure_version && (
                  <span className="ml-1 text-xs text-blue-700">
                    (version {pre.disclosure_version})
                  </span>
                )}
              </span>
            </label>
          </div>
        )}

        {unknownOutcome && (
          <div
            role="alert"
            className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          >
            <div className="font-medium">
              We couldn&apos;t confirm this message
            </div>
            <div className="mt-1">
              The provider may already have it. It will not be sent again
              automatically, and the contact&apos;s history will update when the
              delivery report arrives. Please don&apos;t resend it.
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SendSMSModal;
